<script setup>
import { onMounted, ref } from 'vue';

import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import KraftchatAIAPI from 'dashboard/api/kraftchatAI';

import { knowledgeContent } from '../../helpers/kraftchatAIKnowledgeConfig';
import KraftchatAIKnowledgeForm from './KraftchatAIKnowledgeForm.vue';
import KraftchatAIKnowledgeList from './KraftchatAIKnowledgeList.vue';
import KraftchatAIKnowledgePdfDialog from './KraftchatAIKnowledgePdfDialog.vue';

const documents = ref([]);
const meta = ref({
  page: 1,
  totalCount: 0,
  perPage: 5,
});

const isCreateFormOpen = ref(false);
const isLoading = ref(false);
const loadError = ref('');
const isCreating = ref(false);
const isCreatingPdf = ref(false);
const savingDocumentId = ref(null);
const deletingDocumentId = ref(null);
const editingDocumentId = ref(null);
const pdfDialogRef = ref(null);

const resolveErrorMessage = (error, fallback) =>
  error.response?.data?.error ||
  error.response?.data?.message ||
  error.message ||
  fallback;

const loadDocuments = async (page = 1) => {
  isLoading.value = true;
  loadError.value = '';

  try {
    const response = await KraftchatAIAPI.getKnowledgeDocuments({ page });

    documents.value = response.data.payload || [];
    meta.value = {
      page: response.data.meta?.page || page,
      totalCount: response.data.meta?.totalCount || documents.value.length,
      perPage: response.data.meta?.perPage || 5,
    };
  } catch (error) {
    // Keep whatever was already loaded on screen - never let a failed
    // reload silently replace real data with an empty list.
    loadError.value = resolveErrorMessage(error, knowledgeContent.loadError);
    useAlert(loadError.value);
  } finally {
    isLoading.value = false;
  }
};

const validatePayload = payload => {
  if (!payload.title.trim()) {
    useAlert(knowledgeContent.titleRequiredError);
    return false;
  }

  if (!payload.content.trim()) {
    useAlert(knowledgeContent.contentRequiredError);
    return false;
  }

  return true;
};

const normalizePayload = payload => ({
  title: payload.title.trim(),
  content: payload.content.trim(),
  active: payload.active,
});

const isPdfDocument = document => document.content_source === 'pdf';

const buildPdfFormData = ({ title, active, file }) => {
  const formData = new FormData();
  formData.append('title', title);
  formData.append('active', active);
  if (file) {
    formData.append('pdf_file', file);
  }
  return formData;
};

const handleCreate = async payload => {
  const requestPayload = normalizePayload(payload);

  if (!validatePayload(requestPayload)) {
    return;
  }

  isCreating.value = true;

  try {
    await KraftchatAIAPI.createKnowledgeDocument(requestPayload);
    useAlert(knowledgeContent.createSuccess);

    isCreateFormOpen.value = false;
    await loadDocuments(1);
  } catch (error) {
    useAlert(resolveErrorMessage(error, knowledgeContent.saveError));
  } finally {
    isCreating.value = false;
  }
};

const handleCreatePdf = async payload => {
  if (!payload.file) {
    useAlert(knowledgeContent.pdfFileRequiredError);
    return;
  }

  if (!payload.title.trim()) {
    useAlert(knowledgeContent.titleRequiredError);
    return;
  }

  isCreatingPdf.value = true;

  try {
    const formData = buildPdfFormData({
      title: payload.title.trim(),
      active: payload.active,
      file: payload.file,
    });
    formData.append('content_source', 'pdf');

    await KraftchatAIAPI.createKnowledgeDocument(formData);
    useAlert(knowledgeContent.createSuccess);

    pdfDialogRef.value?.dialogRef?.close();
    pdfDialogRef.value?.resetForm();
    await loadDocuments(1);
  } catch (error) {
    useAlert(resolveErrorMessage(error, knowledgeContent.saveError));
  } finally {
    isCreatingPdf.value = false;
  }
};

const handleUpdatePdf = async (document, payload) => {
  if (!payload.title.trim()) {
    useAlert(knowledgeContent.titleRequiredError);
    return;
  }

  savingDocumentId.value = document.id;

  try {
    const formData = buildPdfFormData({
      title: payload.title.trim(),
      active: payload.active,
      file: payload.file,
    });

    await KraftchatAIAPI.updateKnowledgeDocument(document.id, formData);
    useAlert(knowledgeContent.updateSuccess);

    editingDocumentId.value = null;
    await loadDocuments(meta.value.page);
  } catch (error) {
    useAlert(resolveErrorMessage(error, knowledgeContent.saveError));
  } finally {
    savingDocumentId.value = null;
  }
};

const handleUpdate = async (document, payload) => {
  if (isPdfDocument(document)) {
    await handleUpdatePdf(document, payload);
    return;
  }

  const requestPayload = normalizePayload(payload);

  if (!validatePayload(requestPayload)) {
    return;
  }

  savingDocumentId.value = document.id;

  try {
    await KraftchatAIAPI.updateKnowledgeDocument(document.id, requestPayload);
    useAlert(knowledgeContent.updateSuccess);

    editingDocumentId.value = null;
    await loadDocuments(meta.value.page);
  } catch (error) {
    useAlert(resolveErrorMessage(error, knowledgeContent.saveError));
  } finally {
    savingDocumentId.value = null;
  }
};

const handleDelete = async document => {
  deletingDocumentId.value = document.id;

  try {
    await KraftchatAIAPI.deleteKnowledgeDocument(document.id);
    useAlert(knowledgeContent.deleteSuccess);

    const shouldFetchPreviousPage =
      documents.value.length === 1 && meta.value.page > 1;

    await loadDocuments(
      shouldFetchPreviousPage ? meta.value.page - 1 : meta.value.page
    );
  } catch (error) {
    useAlert(resolveErrorMessage(error, knowledgeContent.deleteError));
  } finally {
    deletingDocumentId.value = null;
  }
};

const handlePageChange = page => {
  editingDocumentId.value = null;
  loadDocuments(page);
};

const handleOpenCreateForm = () => {
  editingDocumentId.value = null;
  isCreateFormOpen.value = true;
};

const handleOpenPdfDialog = () => {
  editingDocumentId.value = null;
  pdfDialogRef.value?.dialogRef?.open();
};

onMounted(() => {
  loadDocuments();
});
</script>

<template>
  <section class="rounded-xl border border-n-weak bg-n-solid-1 p-6 shadow-sm">
    <div class="max-w-5xl space-y-6">
      <div class="space-y-1">
        <h2 class="text-lg font-semibold text-n-slate-12">
          {{ knowledgeContent.title }}
        </h2>

        <p class="text-sm leading-6 text-n-slate-11">
          {{ knowledgeContent.description }}
        </p>
      </div>

      <div class="rounded-xl border border-n-weak bg-n-background p-4">
        <KraftchatAIKnowledgeForm
          v-if="isCreateFormOpen"
          mode="create"
          :is-saving="isCreating"
          @save="handleCreate"
          @cancel="isCreateFormOpen = false"
        />

        <div v-else class="flex items-start justify-between gap-4">
          <div class="min-w-0 space-y-1">
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{ knowledgeContent.createSummaryTitle }}
            </h3>

            <p class="text-sm leading-6 text-n-slate-11">
              {{ knowledgeContent.createSummaryDescription }}
            </p>
          </div>

          <div class="flex flex-shrink-0 items-center gap-2">
            <NextButton
              type="button"
              outline
              slate
              :label="knowledgeContent.createActionLabel"
              @click="handleOpenCreateForm"
            />

            <NextButton
              type="button"
              outline
              slate
              icon="i-lucide-file-text"
              :label="knowledgeContent.createPdfActionLabel"
              @click="handleOpenPdfDialog"
            />
          </div>
        </div>
      </div>

      <KraftchatAIKnowledgePdfDialog
        ref="pdfDialogRef"
        :is-saving="isCreatingPdf"
        @create="handleCreatePdf"
      />

      <p
        v-if="isLoading"
        class="rounded-xl border border-dashed border-n-weak bg-n-background p-4 text-sm text-n-slate-11"
      >
        {{ knowledgeContent.loadingLabel }}
      </p>

      <KraftchatAIKnowledgeList
        :documents="documents"
        :meta="meta"
        :load-error="loadError"
        :editing-document-id="editingDocumentId"
        :saving-document-id="savingDocumentId"
        :deleting-document-id="deletingDocumentId"
        @edit="editingDocumentId = $event.id"
        @update="handleUpdate"
        @cancel-edit="editingDocumentId = null"
        @delete="handleDelete"
        @page-change="handlePageChange"
        @retry="loadDocuments(meta.page)"
      />
    </div>
  </section>
</template>
